import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Form } from 'antd';
import dayjs from 'dayjs';
import { useShallow } from 'zustand/shallow';
import { API } from '@services/api.service';
import LawWarningsAcknowledged from '@/components/request/LawWarningsAcknowledged';
import { OPERATION } from '@/constants';
import useQueryParamsContext from '@/contexts/query-params/useQueryParamsContext';
import { useNotify } from '@/hooks';
import { cn } from '@/libs/utils';
import { QUERY_KEY } from '@/shared/constants';
import { useSalaryStore } from '@/store/useSalaryStore';
import { showCapacityWarningModal } from '@/ui/capacityWarningModal';
import { isValidDateArray } from '@/utils/dateUtils';
import {
  DATE_FORMATS,
  formatDateForBackend,
  getNextWorkday,
  isEmployeeRequestFormDisabled,
  isShiftLeavesEffective,
} from '@/utils/formUtils';
import HolidayLeaveTable from '../../table/holiday-leave-table/HolidayLeaveTable';
import CustomDatePicker from '../input/CustomDatePicker';
import CustomRangePicker from '../input/CustomRangePicker';
import FormActionButton from '../input/FormActionButton';
import { useLeaveDaysCount } from './useLeaveDaysCount';

export default function HolidayLeaveForm({
  formData,
  typeOfLeave,
  employeeId,
  handleRefetch,
}) {
  const { t } = useTranslation(['menus', 'forms', 'messages']);
  const [markedDates, setMarkedDates] = useState([]);
  const { queryParams, setQueryParams } = useQueryParamsContext();
  const notification = useNotify();
  const queryClient = useQueryClient();

  // Keep the last payload to support "override" resubmission from the warning modal
  const payloadRef = useRef(null);

  const { toggle, setToggle } = useSalaryStore(
    useShallow((state) => ({
      toggle: state.toggle,
      setToggle: state.setToggle,
    }))
  );

  const handleCancelModal = () => {
    if (toggle.leaveExtender) {
      setToggle('leaveExtender', false);

      // * Refetch after submitted
      handleRefetch();
    }
  };

  // * Data from `Employee` (used as fallback)
  const initialHolidayLeaveCalculation = formData?.holidayLeaveCalculation;

  // * Build payload for fresh holiday calculation (same as General Data tab)
  const calculationPayload = useMemo(() => {
    const employmentSettings = formData?.employmentSettings;
    const hlc = formData?.holidayLeaveCalculation;

    if (!employmentSettings?.startDate || !hlc) return null;

    return {
      startDate: formatDateForBackend(
        employmentSettings.startDate,
        DATE_FORMATS.STANDARD
      ),
      ...(employmentSettings.endDate
        ? {
            endDate: formatDateForBackend(
              employmentSettings.endDate,
              DATE_FORMATS.STANDARD
            ),
          }
        : {}),
      employmentType: employmentSettings.employmentType || 'unlimited',
      employeeTrackerType: hlc.employeeTrackerType ?? 'existing',
      numberOfHolidayDaysContract: hlc.numberOfHolidayDaysContract || 0,
      numberOfHolidayDaysPast: hlc.numberOfHolidayDaysPast ?? 0,
      numberOfHolidayDaysUsed: hlc.numberOfHolidayDaysUsed ?? 0,
    };
  }, [formData?.employmentSettings, formData?.holidayLeaveCalculation]);

  // * Fetch fresh holiday calculation (same API as General Data tab uses)
  const { data: freshCalculationData } = useQuery({
    queryKey: [QUERY_KEY.employeeCalculation, employeeId, calculationPayload],
    queryFn: async () =>
      API.employees.retrieveEmployeeHolidayCalculation(calculationPayload),
    enabled: !!calculationPayload && !!employeeId,
    staleTime: 30000, // Cache for 30 seconds
  });

  // * State for dynamically calculated holiday availability based on selected dates
  const [updatedAvailability, setUpdatedAvailability] = useState(null);

  // * Merge fresh calculation with updated availability
  // * Priority: freshCalculation > initialHolidayLeaveCalculation, then override with updatedAvailability
  const holidayLeaveCalculation = useMemo(() => {
    // Use fresh calculation if available, otherwise fall back to stored value
    const baseCalculation =
      freshCalculationData?.calculation || initialHolidayLeaveCalculation;

    if (!baseCalculation) return null;

    return {
      ...baseCalculation,
      // Override currentlyAvailableHolidayDays if we have an updated value from date selection
      ...(updatedAvailability !== null && {
        currentlyAvailableHolidayDays: updatedAvailability,
      }),
    };
  }, [
    freshCalculationData?.calculation,
    initialHolidayLeaveCalculation,
    updatedAvailability,
  ]);

  // * Check if company allows weekend leaves
  const allowWeekendLeaves =
    isShiftLeavesEffective(formData, formData?.company);
  const publicHolidays = useMemo(
    () => formData?.company?.publicHolidays || [],
    [formData?.company?.publicHolidays]
  );

  // * Form instance
  const [form] = Form.useForm();

  // * Initial form data
  const initialValues = {
    dateOfLeave: [],
    dateOfRequestCreate: null,
    firstDayBack: null,
  };

  // * Dynamic form values
  const dateOfLeaveValues = Form.useWatch('dateOfLeave', form);
  const isDisabledSubmit = isValidDateArray(
    dateOfLeaveValues?.map((d) => (d ? d.toISOString() : null))
  );

  // * Enabled fetch data if ok
  const isEnabled = (qp, empId) => {
    const { action, requestId } = qp;
    const hasAction = [OPERATION.view, OPERATION.edit].includes(action);

    return !!requestId && !!empId && !!hasAction;
  };

  // * Fetch data for (view or update)
  const { data: requestData } = useQuery({
    queryKey: [
      QUERY_KEY.request,
      employeeId,
      queryParams.action,
      queryParams.requestId,
    ],
    queryFn: async () => API.requests.getRequestById(queryParams.requestId),
    enabled: isEnabled(queryParams, employeeId),
    retry: false,
  });

  // * Show submitted action and disable input fields if not `ACCEPTED` or not `edit` mode
  const hasSubmitted = isEmployeeRequestFormDisabled(
    queryParams?.action ?? '',
    requestData
  );

  // * Set `initial data` from (update or view)
  useEffect(() => {
    if (requestData) {
      const { dateFrom, dateTo, dateOfCreation, firstDayBack } = requestData;

      form.setFieldsValue({
        dateOfLeave: [dayjs(dateFrom), dayjs(dateTo)],
        dateOfRequestCreate: dateOfCreation ? dayjs(dateOfCreation) : null,
        firstDayBack: firstDayBack ? dayjs(firstDayBack) : null,
      });
    }
  }, [form, requestData]);

  // * Calculation (Total Selected Days) — shift-aware (rota-based for shift workers)
  const totalSelectedDays = useLeaveDaysCount({
    employeeId: formData?._id,
    dateOfLeaveValues,
    allowWeekendLeaves,
    publicHolidays,
  });

  // * Calculation (Next day of working)
  const nextWorkingDay = useMemo(() => {
    return getNextWorkday(dateOfLeaveValues, allowWeekendLeaves, publicHolidays);
  }, [dateOfLeaveValues, allowWeekendLeaves, publicHolidays]);

  // * Auto-populate firstDayBack when nextWorkingDay changes
  // Only for new requests with weekendLeaves enabled
  useEffect(() => {
    if (allowWeekendLeaves && nextWorkingDay && !hasSubmitted && !requestData) {
      form.setFieldValue('firstDayBack', dayjs(nextWorkingDay));
    }
  }, [allowWeekendLeaves, nextWorkingDay, form, hasSubmitted, requestData]);

  // * Handle reset form
  const onReset = useCallback(() => {
    form.resetFields();
  }, [form]);

  // * After submitted form (create or edit) redirect to `route=hr-documents`
  const goToHrDocuments = useCallback(() => {
    setQueryParams({ ['route']: 'hr-documents', ['action']: undefined });
  }, [setQueryParams]);

  // * Reset form if not ACTION=(view | edit) from query params of url
  useEffect(() => {
    if (!queryParams.action) {
      onReset();
    }
  }, [queryParams.action, onReset]);

  // * Handle submit form (Create or Edit)
  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (payload) => {
      if (queryParams.action === 'edit') {
        return API.requests.updateRequestById(queryParams.requestId, payload);
      }
      return API.requests.createNewRequest(payload);
    },
    onSuccess: async (response) => {
      // Intercept soft-warning (capacity check) from backend
      if (response?.warning) {
        showCapacityWarningModal({
          t,
          response,
          // `reason` is set when the first-part warning was confirmed.
          onOk: async ({ reason } = {}) => {
            const nextPayload =
              payloadRef.current instanceof FormData
                ? (() => {
                    const fd = payloadRef.current;
                    fd.set('override', 'true');
                    fd.set('overrideLawWarning', 'true');
                    if (reason) fd.set('lawWarningReason', reason);
                    return fd;
                  })()
                : {
                    ...payloadRef.current,
                    override: true,
                    overrideLawWarning: true,
                    ...(reason ? { lawWarningReason: reason } : {}),
                  };

            await mutateAsync(nextPayload);
          },
        });
        return;
      }

      notification.success({
        message: t('success', { ns: 'messages' }),
        description: response.message,
      });

      // * Invalidate queries to refresh employee data and requests
      queryClient.invalidateQueries([QUERY_KEY.employee, employeeId]);
      queryClient.invalidateQueries([QUERY_KEY.allDocuments]);
      queryClient.invalidateQueries([QUERY_KEY.request]);

      // * Close modal if open your `LeaveExtenderModal` form modal
      handleCancelModal();

      // * Reset form fields
      onReset();

      // * Redirect to `route=hr-documents` after submitted form
      goToHrDocuments();
    },
    onError: (error) => {
      notification.error({
        message: error.response?.data?.message,
        description: error.response?.data.error,
      });
    },
  });

  const getMarkedDates = (items) => {
    if (!Array.isArray(items) || items.length === 0) return [];
    return items.map((item) => ({ ...item, disabled: true }));
  };

  // * Fetch data for `markedDates` (disabled dates)
  const { mutateAsync: mutateAsyncDate } = useMutation({
    mutationFn: async (payload) => API.requests.getUsedDates(payload),
    onSuccess: (response) => {
      const md = getMarkedDates(response.items);
      setMarkedDates(md);
    },
    onError: (error) => {
      const errorMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message;

      notification.error({
        message: t('error', { ns: 'messages' }),
        description: errorMsg,
      });
    },
  });

  const handleMonthChange = useCallback(
    (start, end) => {
      const payload = {
        employeeId: formData._id,
        startDate: formatDateForBackend(start, DATE_FORMATS.STANDARD),
        endDate: formatDateForBackend(end, DATE_FORMATS.STANDARD),
      };

      mutateAsyncDate(payload);
    },
    [formData?._id, mutateAsyncDate]
  );

  useEffect(() => {
    if (!formData?._id) return;

    const start = dayjs().startOf('month');
    const end = dayjs().add(1, 'month').endOf('month');

    handleMonthChange(start, end);
  }, [formData?._id, handleMonthChange]);

  // * Mutation for recalculating holiday availability based on selected leave dates
  const { mutateAsync: mutateAvailability } = useMutation({
    mutationFn: async (payload) => {
      return await API.employees.retrieveHolidayAvailabilityCalculation(
        payload
      );
    },
    onSuccess: (response) => {
      if (response?.calculation !== undefined) {
        setUpdatedAvailability(response.calculation);
      }
    },
    onError: (error) => {
      console.error('Failed to recalculate holiday availability:', error);
      // Don't show notification for this - just keep using initial value
    },
  });

  // * Recalculate holiday availability when leave dates change
  useEffect(() => {
    // Only recalculate if we have valid date range selected
    if (!dateOfLeaveValues || dateOfLeaveValues.length !== 2) {
      // Reset to initial value when dates are cleared
      setUpdatedAvailability(null);
      return;
    }

    const [dateFrom, dateTo] = dateOfLeaveValues;
    if (!dateFrom || !dateTo) {
      setUpdatedAvailability(null);
      return;
    }

    // Use fresh calculation if available, otherwise fall back to stored values
    const hlc =
      freshCalculationData?.calculation || initialHolidayLeaveCalculation;
    const employmentSettings = formData?.employmentSettings;

    if (!hlc || !employmentSettings?.startDate) {
      return;
    }

    // Use the END date of the leave period as the calculation date
    // This shows how many days will be available by the time the leave ends
    // NOTE: Do NOT pass holidayDaysYear here - it would incorrectly adjust the calculation
    // date to the previous year when the employee has past days carried over
    const payload = {
      startDate: employmentSettings.startDate,
      endDate: employmentSettings.endDate || null,
      numberOfHolidayDaysPast: hlc.numberOfHolidayDaysPast || 0,
      numberOfHolidayDaysUsed: hlc.numberOfHolidayDaysUsed || 0,
      numberOfHolidayDaysContract: hlc.numberOfHolidayDaysContract || 0,
      numberOfAdditionalHolidayDays:
        hlc.numberOfAdditionalHolidayDays || 0,
      currentDate: dateTo.toISOString(),
    };

    mutateAvailability(payload);
  }, [
    dateOfLeaveValues,
    freshCalculationData?.calculation,
    initialHolidayLeaveCalculation,
    formData?.employmentSettings,
    mutateAvailability,
  ]);

  const onFinish = async (values) => {
    const { _id: empId, company, firstName, lastName } = formData;
    const { dateOfLeave, dateOfRequestCreate, firstDayBack } = values;
    const [dateFrom, dateTo] = dateOfLeave;

    const payload = {
      employee: empId,
      company: company._id,
      firstName,
      lastName,
      dateFrom: formatDateForBackend(dateFrom, DATE_FORMATS.STANDARD),
      dateTo: formatDateForBackend(dateTo, DATE_FORMATS.STANDARD),
      numOfDays: totalSelectedDays.toString(),
      dateOfCreation: formatDateForBackend(
        dateOfRequestCreate,
        DATE_FORMATS.STANDARD
      ),
      typeOfLeave: typeOfLeave,
      override: false,
      overrideLawWarning: false,
    };

    // Include firstDayBack only if weekendLeaves is enabled
    if (allowWeekendLeaves && firstDayBack) {
      payload.firstDayBack = formatDateForBackend(
        firstDayBack,
        DATE_FORMATS.STANDARD
      );
    } else {
      // Otherwise use auto-calculated value
      payload.firstDayBack = formatDateForBackend(
        nextWorkingDay,
        DATE_FORMATS.STANDARD
      );
    }

    payloadRef.current = payload;

    if (!hasSubmitted) {
      await mutateAsync(payload);
    } else {
      console.warn('Please check your hasSubmitted value', hasSubmitted);
    }
  };

  return (
    <Form
      form={form}
      name={`employee_request-holidayLeave-form_${employeeId}`}
      initialValues={initialValues}
      layout="vertical"
      onFinish={onFinish}
      autoComplete="off"
      className={cn('form grid gap-4')}
    >
      <LawWarningsAcknowledged acks={requestData?.lawWarningsAcknowledged} />

      <div className="rounded-md border bg-zinc-100 p-4">
        <div className="grid gap-x-4 md:grid-cols-2">
          <Form.Item
            label={t('dateOfLeave', { ns: 'forms' })}
            name="dateOfLeave"
            rules={[
              {
                required: true,
                message: t('formValidation.selectDate', { ns: 'forms' }),
              },
            ]}
          >
            <CustomRangePicker
              form={form}
              markedDates={markedDates}
              onMonthChange={handleMonthChange}
              disabled={hasSubmitted}
              allowWeekendLeaves={allowWeekendLeaves}
              publicHolidays={publicHolidays}
            />
          </Form.Item>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item label={t('numberOfDays', { ns: 'forms' })}>
              <div
                className={cn(
                  'flex h-8 w-full cursor-not-allowed items-center rounded-md border border-zinc-300 bg-zinc-100 px-3',
                  hasSubmitted ? 'bg-zinc-200 opacity-75' : ''
                )}
              >
                <p>{totalSelectedDays || '~'}</p>
              </div>
            </Form.Item>

            <Form.Item
              label={t('firstDayBack', { ns: 'forms' })}
              name="firstDayBack"
            >
              {allowWeekendLeaves ? (
                <CustomDatePicker
                  form={form}
                  name="firstDayBack"
                  disabled={hasSubmitted}
                />
              ) : (
                <div
                  className={cn(
                    'flex h-8 w-full cursor-not-allowed items-center rounded-md border border-zinc-300 bg-zinc-100 px-3',
                    hasSubmitted ? 'bg-zinc-200 opacity-75' : ''
                  )}
                >
                  <p>
                    {nextWorkingDay
                      ? dayjs(nextWorkingDay).format(DATE_FORMATS.DISPLAY_DOTS)
                      : '~'}
                  </p>
                </div>
              )}
            </Form.Item>
          </div>
        </div>

        <Form.Item
          label={t('dateOfRequestCreation', { ns: 'forms' })}
          name="dateOfRequestCreate"
        >
          <CustomDatePicker form={form} disabled={hasSubmitted} />
        </Form.Item>
      </div>

      {holidayLeaveCalculation && (
        <HolidayLeaveTable holidayLeaveCalculation={holidayLeaveCalculation} />
      )}

      {!hasSubmitted && (
        <FormActionButton
          isPending={isPending}
          isDisabled={!isDisabledSubmit}
          onReset={onReset}
        />
      )}
    </Form>
  );
}
